"use client";

import React, { useCallback, useRef, useState } from "react";
import { useGoogleDriveStatus } from "@/lib/hooks/google-drive/use-google-drive-status";
import { useGoogleDriveBreadcrumbs } from "@/lib/hooks/google-drive/use-google-drive-breadcrumbs";
import { GoogleDriveBrowser } from "@/components/crm/drive/GoogleDriveBrowser";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  LoadingSpinner,
  EmptyState,
} from "@wtc/ui";
import { HardDrive, Upload, FolderOpen } from "lucide-react";
import { useUniversalAuth, useAuth } from "@wtc/ui";
import type { CompanyEntity } from "@/lib/types/company";
import { companyDriveFolderUrl } from "@/lib/integrations/google-drive/customer-root";

interface CompanyDriveProps {
  companyId: string;
  company?: CompanyEntity;
}

export const CompanyDrive: React.FC<CompanyDriveProps> = ({ companyId, company }) => {
  const { data: status, isLoading: statusLoading } = useGoogleDriveStatus();
  const { session } = useUniversalAuth();
  const { getAuthToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadState, setUploadState] = useState<
    { kind: "idle" | "uploading" } | { kind: "done" | "error"; message: string }
  >({ kind: "idle" });

  const handleUploadFile = useCallback(
    async (file: File) => {
      setUploadState({ kind: "uploading" });
      try {
        const token = await getAuthToken();
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`/api/companies/${companyId}/drive/upload`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          credentials: "include",
          body: fd,
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error || `Upload failed (${res.status})`);
        }
        setUploadState({ kind: "done", message: `Uploaded "${file.name}" to the company folder.` });
      } catch (err) {
        setUploadState({
          kind: "error",
          message: err instanceof Error ? err.message : "Upload failed",
        });
      }
    },
    [companyId, getAuthToken],
  );

  const hasAnyDriveAccess =
    !!status?.primary?.hasDriveAccess || !!status?.secondary?.hasDriveAccess;

  // The folder id is stored directly on the company — never resolved by name.
  const storedFolderId = company?.google_drive_folder_id?.trim() || undefined;

  // Breadcrumbs are computed off the stored folder id only (hook called
  // unconditionally to keep hook order stable; no-op when there is no id).
  const { breadcrumbs: reusableBreadcrumbs, parentName: reusableParentName } =
    useGoogleDriveBreadcrumbs({
      enabled: hasAnyDriveAccess && !!storedFolderId,
      targetFolderId: storedFolderId,
    });

  // Single return to keep hooks count consistent across renders
  return (
    <>
      {!statusLoading && (!status || !hasAnyDriveAccess) ? (
        <Card>
          <CardHeader>
            <CardTitle>Google Drive not configured</CardTitle>
            <CardDescription>Connect your Google account to access customer files.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href="/crm/calendar/setup">Go to Drive setup</a>
            </Button>
          </CardContent>
        </Card>
      ) : statusLoading ? (
        <div className="flex items-center gap-2 py-6 text-muted-foreground">
          <LoadingSpinner className="h-5 w-5" />
          Checking Drive status...
        </div>
      ) : !storedFolderId ? (
        <EmptyState
          icon={HardDrive}
          title="Drive folder not provisioned yet"
          description="This company doesn't have a Google Drive folder linked yet. It will appear here automatically once the folder has been provisioned — nothing to do in the meantime."
        />
      ) : (
        <>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <a
                href={companyDriveFolderUrl(storedFolderId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                Open folder in Drive
              </a>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploadState.kind === "uploading"}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploadState.kind === "uploading" ? "Uploading…" : "Upload to company folder"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleUploadFile(f);
                e.target.value = "";
              }}
            />
            {(uploadState.kind === "done" || uploadState.kind === "error") && (
              <span
                className={`text-sm ${uploadState.kind === "error" ? "text-destructive" : "text-muted-foreground"}`}
              >
                {uploadState.message}
              </span>
            )}
          </div>
          <GoogleDriveBrowser
            showHeader={true}
            maxHeight="600px"
            initialParentId={storedFolderId}
            initialParentName={reusableParentName || company?.name || "Folder"}
            initialBreadcrumbs={reusableBreadcrumbs}
            breadcrumbLoading={reusableBreadcrumbs.length === 0}
            compactMode={false}
            allowDownload={true}
            allowView={true}
            allowCreateFolder={true}
            allowUpload={true}
            className="mt-2"
          />
        </>
      )}
    </>
  );
};
